// Phase 5C: everyone holding (or pending) a Compass login in the active
// tenant, normalized into UserProfile-shaped rows — shared by Settings'
// Users & Access card and Contacts' "give this person a login" lookup.
// Replaces both files' old `useCollection<UserProfile>('users', ...)`
// against the legacy email-keyed plane.
//
// Sources, combined:
//   - tenantMemberships (claimed — has a real Firebase Auth uid)
//   - tenantInvites (pending — granted but never signed in yet)
// Both carry a denormalized email/displayName copy (see
// TenantMembership.email in src/types.ts) so no separate per-row identity
// read is needed — and Key 3 (who cannot bulk-read uid-keyed identity docs
// at all, see firestore.rules' users/{userId} read rule) sees the same
// data admin does, scoped to their own district by the SAME rule tiering
// tenantMemberships/tenantInvites already enforce.

import { useMemo } from 'react';
import { where } from 'firebase/firestore';
import { useCollection } from './useCollection';
import { tenantWhere } from '../lib/tenant';
import { isAdmin } from '../lib/visibility';
import type { TenantInvite, TenantMembership, UserProfile } from '../types';

function membershipToProfile(m: TenantMembership): UserProfile {
  return {
    id: m.uid,
    email: m.email ?? '',
    role: m.role,
    districtId: m.districtId ?? null,
    positions: m.positions ?? [],
    committees: m.committees ?? null,
    districtView: m.districtView ?? null,
    keyThree: m.keyThree ?? null,
    councilWide: m.councilWide ?? null,
    councilAccess: m.councilAccess ?? null,
    contactAccess: m.contactAccess ?? null,
    contactId: m.contactId ?? null,
    displayName: m.displayName ?? undefined,
    createdAt: m.createdAt,
  };
}

function inviteToProfile(inv: TenantInvite): UserProfile {
  return {
    id: inv.id, // synthetic — no real uid until this invite is claimed
    email: inv.email,
    role: inv.role,
    districtId: inv.districtId ?? null,
    positions: inv.positions ?? [],
    committees: inv.committees ?? null,
    districtView: inv.districtView ?? null,
    keyThree: inv.keyThree ?? null,
    councilWide: inv.councilWide ?? null,
    councilAccess: inv.councilAccess ?? null,
    contactAccess: inv.contactAccess ?? null,
    contactId: inv.contactId ?? null,
    displayName: inv.displayName ?? undefined,
    createdAt: inv.createdAt,
  };
}

/** `districtId`, when passed for a non-admin caller, scopes both queries
 *  to that one district — matching firestore.rules' Key 3 read tier
 *  exactly (admin passes none and reads every district). `enabled` mirrors
 *  the old canGrantElevated gate: a plain chair never gets these rows at
 *  all (matches their old empty usersByEmail). */
export function useLoginProfiles(
  profile: UserProfile | null,
  enabled: boolean,
): { data: UserProfile[]; claimed: UserProfile[]; pending: UserProfile[]; loading: boolean; error: Error | null } {
  const admin = isAdmin(profile);
  const districtConstraint =
    admin || !profile?.districtId ? [] : [where('districtId', '==', profile.districtId)];

  const memberships = useCollection<TenantMembership>(
    'tenantMemberships',
    [tenantWhere(), ...districtConstraint],
    enabled,
  );
  const invites = useCollection<TenantInvite>(
    'tenantInvites',
    [tenantWhere(), ...districtConstraint],
    enabled,
  );

  const claimed = useMemo(() => memberships.data.map(membershipToProfile), [memberships.data]);
  const pending = useMemo(() => invites.data.map(inviteToProfile), [invites.data]);
  const data = useMemo(() => [...claimed, ...pending], [claimed, pending]);

  return {
    data,
    claimed,
    pending,
    loading: memberships.loading || invites.loading,
    error: memberships.error ?? invites.error,
  };
}
